import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box, Toolbar, Typography, CircularProgress, Alert } from "@mui/material";
import sizeConfigs from "../../configs/sizeConfigs";
import Sidebar from "../common/Sidebar";
import ChatBotIcon from "../../assets/images/chatBot1.png";
import ChatBot from "../../pages/ChatBot/ChatBot";
import Draggable from "react-draggable";
import CookieConsent from "../../utils/CookieConsent";
import { useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import Cookies from 'js-cookie';
import api from '../../utils/Url';
import { toast } from 'react-toastify';

interface MenuItem {
  menuId: number;
  menuName: string;
  parentId: number | null;
  pageUrl: string | null;
  icon: string | null;
  displayNo: number | null;
  isMenu: boolean;
  isAdd: boolean;
  isEdit: boolean;
  isDel: boolean;
  isView: boolean | null;
  isPrint: boolean;
  isExport: boolean;
  isRelease: boolean;
  isPost: boolean;
  vtsCombine: any;
  hasSubMenu: boolean;
  subMenu: MenuItem[];
}

interface ApiResponse {
  isSuccess: boolean;
  code: number;
  internalSuccess: boolean;
  isTechError: boolean;
  mesg: string | null;
  data: MenuItem[];
}

const MainLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [isHovered, setIsHovered] = useState(false);
  const [isChatBotOpen, setIsChatBotOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get user details from Redux store
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const isAuthenticated = useSelector((state: RootState) => state.user.isAuthenticated);

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!isAuthenticated && location.pathname !== "/") {
      navigate("/");
      return;
    }

    // Fetch menu if authenticated and on protected routes
    if (isAuthenticated && userDetails?.userName && location.pathname !== "/") {
      fetchMenu();
    } else if (location.pathname === "/" && isAuthenticated) {
      // If on login page but authenticated, redirect to home
      navigate("/home");
    }
  }, [isAuthenticated, location.pathname, navigate, userDetails]);

  const fetchMenu = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get access token from cookies
      const token = Cookies.get('access_token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      if (!userDetails?.userName) {
        throw new Error('User information not available');
      }

      // Make API call to get menu permissions
      const response = await api.get<ApiResponse>('/Adm/GetMenuUser', {
        params:{
          userName: userDetails.userName
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data?.isSuccess && response.data.data) {
        // Build hierarchical menu structure from API response
        const hierarchicalMenu = buildHierarchicalMenu(response.data.data);
        setMenuItems(hierarchicalMenu);
        
        // Store in sessionStorage for quick access if needed
        sessionStorage.setItem('menuData', JSON.stringify(hierarchicalMenu));
      } else {
        throw new Error(response.data?.mesg || 'Failed to fetch menu data');
      }
    } catch (error: any) {
      console.error('Error fetching menu:', error);
      setError(error.message || 'Failed to load menu. Please try again.');
      toast.error('Failed to load menu permissions');
      
      // If unauthorized, redirect to login
      if (error.response?.status === 401) {
        navigate("/");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const buildHierarchicalMenu = (apiData: MenuItem[]): any[] => {
    // Get root menus (parentId === null)
    const rootMenus = apiData.filter(item => item.parentId === null);
    
    // Define preferred menu order for root menus
    const parentMenuOrder = [
      "Vehicle Management",
      "Employee Info",
      "Email System",
      "Reports",
      "Help",
      "Admin",
    ];
    
    // Sort root menus by predefined order
    rootMenus.sort((a, b) => {
      const indexA = parentMenuOrder.indexOf(a.menuName);
      const indexB = parentMenuOrder.indexOf(b.menuName);
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return (a.menuName || '').localeCompare(b.menuName || '');
    });
    
    // Process each root menu recursively
    const processMenuItem = (menuItem: MenuItem, parentPath = ""): any => {
      // Check if this item has subMenu (folder) or is a leaf (file)
      const hasChildren = menuItem.subMenu && menuItem.subMenu.length > 0;
      
      // Generate path
      const sanitizedName = menuItem.menuName?.replace(/\s+/g, "").toLowerCase() || '';
      const currentPath = `${parentPath}/${sanitizedName}`;
      
      // Process children if they exist
      let children: any[] = [];
      if (hasChildren && menuItem.subMenu) {
        // Sort children by displayNo or menuName
        const sortedChildren = [...menuItem.subMenu].sort((a, b) => {
          if (a.displayNo !== null && b.displayNo !== null) {
            return (a.displayNo || 0) - (b.displayNo || 0);
          }
          return (a.menuName || '').localeCompare(b.menuName || '');
        });
        
        // Recursively process children
        children = sortedChildren.map(child => processMenuItem(child, currentPath));
      }
      
      return {
        menuId: menuItem.menuId,
        parentId: menuItem.parentId,
        menuName: menuItem.menuName,
        pageUrl: menuItem.pageUrl,
        icon: menuItem.icon,
        displayNo: menuItem.displayNo,
        isMenu: menuItem.isMenu,
        hasSubMenu: hasChildren,
        children: children,
        path: currentPath,
        originalSubMenu: menuItem.subMenu,
        // Set all permissions to true for testing
        permissions: {
          isAdd: true,
          isEdit: true,
          isDel: true,
          isView: true,
          isPrint: true,
          isExport: true,
          isRelease: true,
          isPost: true
        }
      };
    };
    
    // Process all root menus
    return rootMenus.map(menu => processMenuItem(menu));
  };

  const handleMenuClick = (e: any, item: any) => {
    const menuId = item.menuId;
    const menuName = item.menuName;
    
    // For testing: Set all permissions to true
    const testPermissions = {
      isAdd: true,
      isEdit: true,
      isDel: true,
      isView: true,
      isPrint: true,
      isExport: true,
      isRelease: true,
      isPost: true
    };

    // Only navigate if it's a leaf node (no children) AND has a pageUrl
    if (item.children && item.children.length > 0) {
      // This is a folder, don't navigate - let sidebar handle expand/collapse
      // Store permissions for this menu item
      sessionStorage.setItem("menuId", menuId.toString());
      sessionStorage.setItem("menuName", menuName);
      sessionStorage.setItem("menuPermissions", JSON.stringify(testPermissions));
      return;
    }

    const path = `${item.path}?appId=${menuId}&Appname=${menuName}.aspx`;

    if (!path || !item.pageUrl) {
      toast.error("Path Not Found or Page URL not available");
    } else {
      sessionStorage.setItem("menuId", menuId.toString());
      sessionStorage.setItem("menuName", menuName);
      sessionStorage.setItem("menuPermissions", JSON.stringify(testPermissions));
      navigate(path);
    }
  };

  // If loading, show loading indicator
  if (isLoading && location.pathname !== "/") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: `var(--main-background)`,
        }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading menu...
        </Typography>
      </Box>
    );
  }

  // If error, show error message
  if (error && location.pathname !== "/") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: `var(--main-background)`,
          flexDirection: "column",
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <button 
          onClick={fetchMenu}
          style={{
            padding: "10px 20px",
            backgroundColor: "#003380",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </Box>
    );
  }

  console.log('menuItems structure for sidebar:', menuItems);

  return (
    <div>
      {location.pathname === "/" ? (
        <Outlet />
      ) : (
        <div>
          <Box sx={{ display: "flex" }}>
            <Sidebar items={menuItems} onClick={handleMenuClick} />
            <Box
              sx={{
                flexGrow: 1,
                px: 2,
                py: 3,
                width: `calc(100% - ${sizeConfigs.sidebar.width})`,
                minHeight: "100vh",
                backgroundColor: `var(--main-background)`,
                backgroundImage: `var(--background-image)`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            >
              <Toolbar />
              <Outlet />
              <Draggable>
                <Box
                  sx={{
                    position: "fixed",
                    bottom: 20,
                    right: 3,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {isHovered && (
                    <Typography
                      variant="body2"
                      sx={{
                        marginRight: 1,
                        background: "rgba(0, 0, 0, 0.7)",
                        color: "white",
                        padding: "5px 10px",
                        borderRadius: "5px",
                        position: "absolute",
                        bottom: "30px",
                        right: "40px",
                      }}
                    >
                      Ask Me...
                    </Typography>
                  )}
                  <Box
                    component="img"
                    src={ChatBotIcon}
                    alt="Chatbot"
                    sx={{
                      width: 50,
                      height: 50,
                      cursor: "pointer",
                      "&:hover": {
                        opacity: 0.8,
                      },
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    onClick={() => setIsChatBotOpen(true)}
                  />
                </Box>
              </Draggable>
            </Box>
          </Box>

          <CookieConsent />
          <ChatBot open={isChatBotOpen} onClose={() => setIsChatBotOpen(false)} />
        </div>
      )}
    </div>
  );
};

export default MainLayout;



// import { Outlet, useLocation, useNavigate } from "react-router-dom";
// import { Box, Toolbar, Typography, CircularProgress, Alert } from "@mui/material";
// import sizeConfigs from "../../configs/sizeConfigs";
// import Sidebar from "../common/Sidebar";
// import ChatBotIcon from "../../assets/images/chatBot1.png";
// import ChatBot from "../../pages/ChatBot/ChatBot";
// import Draggable from "react-draggable";
// import CookieConsent from "../../utils/CookieConsent";
// import { useEffect, useState } from "react";
// import { useSelector } from 'react-redux';
// import { RootState } from '../../redux/store';
// import Cookies from 'js-cookie';
// import api from '../../utils/Url';
// import { toast } from 'react-toastify';

// interface MenuItem {
//   menuId: number;
//   menuName: string;
//   parentId: number | null;
//   pageUrl: string | null;
//   icon: string | null;
//   displayNo: number | null;
//   isMenu: boolean;
//   isAdd: boolean;
//   isEdit: boolean;
//   isDel: boolean;
//   isView: boolean | null;
//   isPrint: boolean;
//   isExport: boolean;
//   isRelease: boolean;
//   isPost: boolean;
//   vtsCombine: any;
//   hasSubMenu: boolean;
//   subMenu: MenuItem[];
// }

// interface ApiResponse {
//   isSuccess: boolean;
//   code: number;
//   internalSuccess: boolean;
//   isTechError: boolean;
//   mesg: string | null;
//   data: MenuItem[];
// }

// const MainLayout = () => {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const [menuItems, setMenuItems] = useState<any[]>([]);
//   const [isHovered, setIsHovered] = useState(false);
//   const [isChatBotOpen, setIsChatBotOpen] = useState(false);
//   const [isLoading, setIsLoading] = useState(true);
//   const [error, setError] = useState<string | null>(null);

//   // Get user details from Redux store
//   const userDetails = useSelector((state: RootState) => state.user.userDetails);
//   const isAuthenticated = useSelector((state: RootState) => state.user.isAuthenticated);

//   useEffect(() => {
//     // Redirect to login if not authenticated
//     if (!isAuthenticated && location.pathname !== "/") {
//       navigate("/");
//       return;
//     }

//     // Fetch menu if authenticated and on protected routes
//     if (isAuthenticated && userDetails?.userName && location.pathname !== "/") {
//       fetchMenu();
//     } else if (location.pathname === "/" && isAuthenticated) {
//       // If on login page but authenticated, redirect to home
//       navigate("/home");
//     }
//   }, [isAuthenticated, location.pathname, navigate, userDetails]);

//   const fetchMenu = async () => {
//     try {
//       setIsLoading(true);
//       setError(null);

//       // Get access token from cookies
//       const token = Cookies.get('access_token');
      
//       if (!token) {
//         throw new Error('No authentication token found');
//       }

//       if (!userDetails?.userName) {
//         throw new Error('User information not available');
//       }

//       // Make API call to get menu permissions
//       const response = await api.get<ApiResponse>('/Adm/GetMenuUser', {
//         params:{
//           userName: userDetails.userName
//         },
//         headers: {
//           Authorization: `Bearer ${token}`
//         }
//       });

//       if (response.data?.isSuccess && response.data.data) {
//         // Build hierarchical menu structure from API response
//         const hierarchicalMenu = buildHierarchicalMenu(response.data.data);
//         setMenuItems(hierarchicalMenu);
        
//         // Store in sessionStorage for quick access if needed
//         sessionStorage.setItem('menuData', JSON.stringify(hierarchicalMenu));
//       } else {
//         throw new Error(response.data?.mesg || 'Failed to fetch menu data');
//       }
//     } catch (error: any) {
//       console.error('Error fetching menu:', error);
//       setError(error.message || 'Failed to load menu. Please try again.');
//       toast.error('Failed to load menu permissions');
      
//       // If unauthorized, redirect to login
//       if (error.response?.status === 401) {
//         navigate("/");
//       }
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const buildHierarchicalMenu = (apiData: MenuItem[]): any[] => {
//     // Get root menus (parentId === null)
//     const rootMenus = apiData.filter(item => item.parentId === null);
    
//     // Define preferred menu order for root menus
//     const parentMenuOrder = [
//       "Vehicle Management",
//       "Employee Info",
//       "Email System",
//       "Reports",
//       "Help",
//       "Admin",
//     ];
    
//     // Sort root menus by predefined order
//     rootMenus.sort((a, b) => {
//       const indexA = parentMenuOrder.indexOf(a.menuName);
//       const indexB = parentMenuOrder.indexOf(b.menuName);
//       if (indexA !== -1 && indexB !== -1) {
//         return indexA - indexB;
//       }
//       if (indexA !== -1) return -1;
//       if (indexB !== -1) return 1;
//       return (a.menuName || '').localeCompare(b.menuName || '');
//     });
    
//     // Process each root menu recursively
//     const processMenuItem = (menuItem: MenuItem, parentPath = ""): any => {
//       // Check if this item has subMenu (folder) or is a leaf (file)
//       const hasChildren = menuItem.subMenu && menuItem.subMenu.length > 0;
      
//       // Generate path
//       const sanitizedName = menuItem.menuName?.replace(/\s+/g, "").toLowerCase() || '';
//       const currentPath = `${parentPath}/${sanitizedName}`;
      
//       // Process children if they exist
//       let children: any[] = [];
//       if (hasChildren && menuItem.subMenu) {
//         // Sort children by displayNo or menuName
//         const sortedChildren = [...menuItem.subMenu].sort((a, b) => {
//           if (a.displayNo !== null && b.displayNo !== null) {
//             return (a.displayNo || 0) - (b.displayNo || 0);
//           }
//           return (a.menuName || '').localeCompare(b.menuName || '');
//         });
        
//         // Recursively process children
//         children = sortedChildren.map(child => processMenuItem(child, currentPath));
//       }
      
//       return {
//         menuId: menuItem.menuId,
//         parentId: menuItem.parentId,
//         menuName: menuItem.menuName,
//         pageUrl: menuItem.pageUrl,
//         icon: menuItem.icon,
//         displayNo: menuItem.displayNo,
//         isMenu: menuItem.isMenu,
//         hasSubMenu: menuItem.hasSubMenu,
//         // Convert hasSubMenu to children for sidebar compatibility
//         children: children,
//         path: currentPath,
//         // Store original subMenu for reference if needed
//         originalSubMenu: menuItem.subMenu,
//         permissions: {
//           isAdd: menuItem.isAdd,
//           isEdit: menuItem.isEdit,
//           isDel: menuItem.isDel,
//           isView: menuItem.isView,
//           isPrint: menuItem.isPrint,
//           isExport: menuItem.isExport,
//           isRelease: menuItem.isRelease,
//           isPost: menuItem.isPost
//         }
//       };
//     };
    
//     // Process all root menus
//     return rootMenus.map(menu => processMenuItem(menu));
//   };

//   const handleMenuClick = (e: any, item: any) => {
//     const menuId = item.menuId;
//     const menuName = item.menuName;
    
//     // Check if user has view permission for this menu
//     if (!item.permissions?.isView) {
//       toast.error(`You don't have permission to access ${menuName}`);
//       return;
//     }

//     // Only navigate if it's a leaf node (no children)
//     if (item.children && item.children.length > 0) {
//       // This is a folder, don't navigate, let the sidebar handle expand/collapse
//       return;
//     }

//     const path = `${item.path}?appId=${menuId}&Appname=${menuName}.aspx`;

//     if (!path || !item.pageUrl) {
//       toast.error("Path Not Found or Page URL not available");
//     } else {
//       sessionStorage.setItem("menuId", menuId.toString());
//       sessionStorage.setItem("menuName", menuName);
//       sessionStorage.setItem("menuPermissions", JSON.stringify(item.permissions || {}));
//       navigate(path);
//     }
//   };

//   // If loading, show loading indicator
//   if (isLoading && location.pathname !== "/") {
//     return (
//       <Box
//         sx={{
//           display: "flex",
//           justifyContent: "center",
//           alignItems: "center",
//           height: "100vh",
//           backgroundColor: `var(--main-background)`,
//         }}
//       >
//         <CircularProgress />
//         <Typography variant="h6" sx={{ ml: 2 }}>
//           Loading menu...
//         </Typography>
//       </Box>
//     );
//   }

//   // If error, show error message
//   if (error && location.pathname !== "/") {
//     return (
//       <Box
//         sx={{
//           display: "flex",
//           justifyContent: "center",
//           alignItems: "center",
//           height: "100vh",
//           backgroundColor: `var(--main-background)`,
//           flexDirection: "column",
//         }}
//       >
//         <Alert severity="error" sx={{ mb: 2 }}>
//           {error}
//         </Alert>
//         <button 
//           onClick={fetchMenu}
//           style={{
//             padding: "10px 20px",
//             backgroundColor: "#003380",
//             color: "white",
//             border: "none",
//             borderRadius: "5px",
//             cursor: "pointer",
//           }}
//         >
//           Retry
//         </button>
//       </Box>
//     );
//   }

//   console.log('menuItems structure for sidebar:', menuItems);

//   return (
//     <div>
//       {location.pathname === "/" ? (
//         <Outlet />
//       ) : (
//         <div>
//           <Box sx={{ display: "flex" }}>
//             <Sidebar items={menuItems} onClick={handleMenuClick} />
//             <Box
//               sx={{
//                 flexGrow: 1,
//                 px: 2,
//                 py: 3,
//                 width: `calc(100% - ${sizeConfigs.sidebar.width})`,
//                 minHeight: "100vh",
//                 backgroundColor: `var(--main-background)`,
//                 backgroundImage: `var(--background-image)`,
//                 backgroundSize: "cover",
//                 backgroundPosition: "center",
//                 backgroundRepeat: "no-repeat",
//               }}
//             >
//               <Toolbar />
//               <Outlet />
//               <Draggable>
//                 <Box
//                   sx={{
//                     position: "fixed",
//                     bottom: 20,
//                     right: 3,
//                     display: "flex",
//                     alignItems: "center",
//                   }}
//                 >
//                   {isHovered && (
//                     <Typography
//                       variant="body2"
//                       sx={{
//                         marginRight: 1,
//                         background: "rgba(0, 0, 0, 0.7)",
//                         color: "white",
//                         padding: "5px 10px",
//                         borderRadius: "5px",
//                         position: "absolute",
//                         bottom: "30px",
//                         right: "40px",
//                       }}
//                     >
//                       Ask Me...
//                     </Typography>
//                   )}
//                   <Box
//                     component="img"
//                     src={ChatBotIcon}
//                     alt="Chatbot"
//                     sx={{
//                       width: 50,
//                       height: 50,
//                       cursor: "pointer",
//                       "&:hover": {
//                         opacity: 0.8,
//                       },
//                     }}
//                     onMouseEnter={() => setIsHovered(true)}
//                     onMouseLeave={() => setIsHovered(false)}
//                     onClick={() => setIsChatBotOpen(true)}
//                   />
//                 </Box>
//               </Draggable>
//             </Box>
//           </Box>

//           <CookieConsent />
//           <ChatBot open={isChatBotOpen} onClose={() => setIsChatBotOpen(false)} />
//         </div>
//       )}
//     </div>
//   );
// };

// export default MainLayout;




















// import { Outlet, useLocation, useNavigate } from "react-router-dom";
// import { Box, Toolbar, Typography } from "@mui/material";
// import sizeConfigs from "../../configs/sizeConfigs";
// import Sidebar from "../common/Sidebar";
// import ChatBotIcon from "../../assets/images/chatBot1.png";
// import ChatBot from "../../pages/ChatBot/ChatBot";
// import Draggable from "react-draggable";
// import CookieConsent from "../../utils/CookieConsent";
// import { useEffect, useState } from "react";
// import { useDispatch, useSelector } from 'react-redux';
// import { RootState } from '../../redux/store'; 
// //import { setUserDetails, clearUserDetails } from '../../redux/userSlice';


// const MainLayout = () => {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const [menuItems, setMenuItems] = useState<any>([]);
//   const [isHovered, setIsHovered] = useState(false);
//   const [isChatBotOpen, setIsChatBotOpen] = useState(false);

//   const storedPermissions = useSelector((state: RootState) => state.user.permissions) || [];

  
  

//   console.log('storedPermissions',storedPermissions);

//   useEffect(() => {


//     //console.log('storedPermissions.length',storedPermissions.length)
   
//     if (storedPermissions.length > 0) {

//       console.log('storedPermissions.length',storedPermissions.length)
     
//       // if (location.pathname === "/") {
//       //   navigate("/home"); 
//       // }
//     } else {
      
//       if (location.pathname !== "/") {
//         navigate("/"); 
//       }
//     }
//   }, [storedPermissions, location.pathname, navigate]);

//   useEffect(() => {
//     if (storedPermissions.length > 0) {
//       // If storedPermissions is available, fetch the menu
//       fetchMenu();
//     }
//   }, [storedPermissions]); // Run only when storedPermissions changes

//   const fetchMenu = () => {
//     const buildMenuHierarchy = (permissions: any[]) => {
//       const menuMap = new Map<number, any>();

//       permissions.forEach((perm: any) => {
//         menuMap.set(perm.menuId, {
//           menuId: perm.menuId,
//           parentId: perm.parentId,
//           menuName: perm.menuName,
//           path: "",
//           children: [],
//         });
//       });

//       let rootMenus: any[] = [];

//       menuMap.forEach((menu) => {
//         if (menu.parentId === null) {
//           rootMenus.push(menu);
//         } else {
//           const parentMenu = menuMap.get(menu.parentId);
//           if (parentMenu) {
//             parentMenu.children.push(menu);
//           }
//         }
//       });

//       const parentMenuOrder = [
//         "Vehicle Management",
//         "Employee Info",
//         "Email System",
//         "Reports",
//         "Help",
//         "Admin",
//       ];

//       rootMenus = rootMenus.filter((menu) =>
//         permissions.some((perm) => perm.menuName === menu.menuName)
//       );

//       rootMenus.sort((a, b) => {
//         const indexA = parentMenuOrder.indexOf(a.menuName);
//         const indexB = parentMenuOrder.indexOf(b.menuName);
//         return indexA - indexB;
//       });

//       const filterAndSortChildren: any = (menus: any[]) => {
//         return menus
//           .filter((menu) => menuMap.has(menu.menuId))
//           .map((menu) => ({
//             ...menu,
//             children: filterAndSortChildren(menu.children).sort((a: any, b: any) =>
//               a.menuName.localeCompare(b.menuName)
//             ),
//           }));
//       };

//       const sortedMenus = filterAndSortChildren(rootMenus);

//       const generatePaths = (menus: any[], parentPath = "") => {
//         menus.forEach((menu) => {
//           menu.path = `${parentPath}/${menu.menuName.replace(/\s+/g, "").toLowerCase()}`;
//           if (menu.children.length > 0) {
//             generatePaths(menu.children, menu.path);
//           }
//         });
//       };

//       generatePaths(sortedMenus);
//       return sortedMenus;
//     };

//     const menuHierarchy = buildMenuHierarchy(storedPermissions);
//     setMenuItems(menuHierarchy);
//   };




//   const handleMenuClick = (e: any, item: any) => {
//     const menuId = item.menuId;
//     const menuName = item.menuName;
//     const path = `${item.path}?appId=${menuId}&Appname=${menuName}.aspx`;

//     if (!path) {
//       window.alert("Path Not Found");
//     } else {
//       sessionStorage.setItem("menuId", menuId);
//       sessionStorage.setItem("menuName", menuName);
//       navigate(path);
//     }
//   };

//   return (
//     <div>
//       {location.pathname === "/" ? (
//         <Outlet />
//       ) : (
//         <div>
//           <Box sx={{ display: "flex" }}>
//             <Sidebar items={menuItems} onClick={handleMenuClick} />
//             <Box
//               sx={{
//                 flexGrow: 1,
//                 px: 2,
//                 py: 3,
//                 width: `calc(100% - ${sizeConfigs.sidebar.width})`,
//                 minHeight: "100vh",
//                 backgroundColor: `var(--main-background)`,
//                 backgroundImage: `var(--background-image)`,
//                 backgroundSize: "cover",
//                 backgroundPosition: "center",
//                 backgroundRepeat: "no-repeat",
//               }}
//             >
//               <Toolbar />
//               <Outlet />
//               <Draggable>
//                 <Box
//                   sx={{
//                     position: "fixed",
//                     bottom: 20,
//                     right: 3,
//                     display: "flex",
//                     alignItems: "center",
//                   }}
//                 >
//                   {isHovered && (
//                     <Typography
//                       variant="body2"
//                       sx={{
//                         marginRight: 1,
//                         background: "rgba(0, 0, 0, 0.7)",
//                         color: "white",
//                         padding: "5px 10px",
//                         borderRadius: "5px",
//                         position: "absolute",
//                         bottom: "30px",
//                         right: "40px",
//                       }}
//                     >
//                       Ask Me...
//                     </Typography>
//                   )}
//                   <Box
//                     component="img"
//                     src={ChatBotIcon}
//                     alt="Chatbot"
//                     sx={{
//                       width: 50,
//                       height: 50,
//                       cursor: "pointer",
//                       "&:hover": {
//                         opacity: 0.8,
//                       },
//                     }}
//                     onMouseEnter={() => setIsHovered(true)}
//                     onMouseLeave={() => setIsHovered(false)}
//                     onClick={() => setIsChatBotOpen(true)}
//                   />
//                 </Box>
//               </Draggable>
//             </Box>
//           </Box>

//           <CookieConsent />
//           <ChatBot open={isChatBotOpen} onClose={() => setIsChatBotOpen(false)} />
//         </div>
//       )}
//     </div>
//   );
// };

// export default MainLayout;
